import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from 'app/store/rootReducer';
import { userService, type FeatureFlag } from 'app/services/user.service';
import logger from 'util/logging';

export type FeatureFlagsValue = boolean | 'speedster' | 'desktop' | 'mobile';
export type FeatureFlagsDict = Record<string, FeatureFlagsValue>;

export interface FeatureFlagsState {
  areFeatureFlagsLoaded: boolean;
  flags: { [key: string]: any };
}

export const initialState: FeatureFlagsState = {
  areFeatureFlagsLoaded: false,
  flags: {},
};

export interface GetRemoteFlagsProps {
  email: string;
  paidolId: string;
}

export const getRemoteFlags = createAsyncThunk<FeatureFlag[], GetRemoteFlagsProps>(
  'featureFlags/getRemoteFlags',
  async ({ email, paidolId }) => {
    const flags = await userService.listFeatureFlags(email, paidolId);
    return flags.map((item) => ({
      ...item,
      value: typeof item.value === 'string' ? JSON.parse(item.value) : item.value,
    })) as FeatureFlag[];
  }
);

/**
 * Loads the whitelisted "public" subset of flags via IAM auth. Used during the
 * pre-paidol onboarding flow where there is no Cognito session yet, so the
 * standard `getRemoteFlags` (which requires email + paidolId) cannot run.
 */
export const getPublicFlags = createAsyncThunk<FeatureFlag[]>('featureFlags/getPublicFlags', async () => {
  const flags = await userService.listPublicFeatureFlags();
  return flags.map((item) => ({
    ...item,
    value: typeof item.value === 'string' ? JSON.parse(item.value) : item.value,
  })) as FeatureFlag[];
});

export const parseConfigValue = (value: string | boolean): FeatureFlagsValue => {
  switch (value) {
    case 'true':
    case 'TRUE':
    case true:
      return true;
    case 'false':
    case 'FALSE':
    case false:
      return false;
    case 'speedster':
    case 'desktop':
    case 'mobile':
      return value;
    default:
      logger().error(`Unknown feature flag value: ${value}; assuming FALSE`);
      return false;
  }
};

const featureFlagsSlice = createSlice({
  name: 'featureFlags',
  initialState,
  reducers: {
    resetFeatureFlagsSlice: () => initialState,
    setFlagsLoaded(state, action) {
      state.areFeatureFlagsLoaded = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getRemoteFlags.fulfilled, (state, action) => {
      action.payload.forEach((item) => {
        const parsedValue = typeof item.value === 'string' ? JSON.parse(item.value) : item.value;
        state.flags[item.name] = parsedValue;
      });
    });
    builder.addCase(getPublicFlags.fulfilled, (state, action) => {
      action.payload.forEach((item) => {
        const parsedValue = typeof item.value === 'string' ? JSON.parse(item.value) : item.value;
        state.flags[item.name] = parsedValue;
      });
    });
  },
});

export const { resetFeatureFlagsSlice, setFlagsLoaded } = featureFlagsSlice.actions;

export const selectFeatureFlags = (state: RootState) => state.featureFlags as FeatureFlagsState;

export default featureFlagsSlice.reducer;
